import { createRoute, Link, useParams } from "@tanstack/react-router";
import { useMe } from "../../lib/me";
import { cx } from "../../ui";
import { workspaceRoute } from "../shell/routes";
import { useSidebarCollapsed } from "../shell/sidebar-state";
import { ChatConversations, useChatList } from "./ChatConversations";
import { Conversation } from "./Conversation";

/**
 * Chat (WP-013): channels and DMs per workspace. `/chat` shows the list (and, from `md` up, an
 * empty conversation pane); `/chat/$channelId` opens one. Below `md` only one of the two is on
 * screen, and the conversation has a link back to the list. From `md` up the list is in the
 * sidebar (WP-056), and back in the page while the sidebar is a rail.
 */
const chatLayoutRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "chat",
  component: ChatScreen,
});

// The screen renders both panes itself; these routes only give each state its URL.
const chatIndexRoute = createRoute({ getParentRoute: () => chatLayoutRoute, path: "/", component: () => null });
const chatChannelRoute = createRoute({ getParentRoute: () => chatLayoutRoute, path: "$channelId", component: () => null });

export const chatRoute = chatLayoutRoute.addChildren([chatIndexRoute, chatChannelRoute]);

function ChatScreen() {
  const { workspaceId } = chatLayoutRoute.useParams();
  const params = useParams({ strict: false });
  const channelId = params.channelId === undefined ? null : params.channelId;
  const me = useMe();
  const { channels, list, members, reads } = useChatList(workspaceId, channelId);
  const open = channelId === null ? null : list.find((c) => c.id === channelId);
  // From `md` up the conversations are in the sidebar (WP-056), unless it is a rail of icons.
  const inSidebar = !useSidebarCollapsed();

  return (
    <div className="flex h-[calc(100dvh-6.5rem)] min-h-80 gap-4 md:h-[calc(100dvh-3rem)] md:gap-0">
      <h1 className="sr-only">Chat</h1>
      <div
        className={cx(
          "min-h-0 w-full flex-col md:w-56 md:shrink-0 md:border-r md:border-border md:pr-2",
          channelId === null ? "flex" : "hidden",
          inSidebar ? "md:hidden" : "md:flex",
        )}
      >
        <ChatConversations workspaceId={workspaceId} channelId={channelId} />
      </div>
      <div className={cx("min-h-0 min-w-0 flex-1 flex-col md:flex", !inSidebar && "md:pl-6", channelId === null ? "hidden" : "flex")}>
        {channelId === null ? (
          <p className="m-auto text-sm text-muted">Pick a channel or a direct message.</p>
        ) : open === undefined ? (
          channels.status === "ready" ? (
            <div className="m-auto flex flex-col items-center gap-2 text-sm">
              <p className="text-muted">That conversation does not exist or is not yours.</p>
              <Link to="/w/$workspaceId/chat" params={{ workspaceId }} className="text-accent underline">
                Back to conversations
              </Link>
            </div>
          ) : null
        ) : open === null ? null : (
          <Conversation
            key={open.id}
            workspaceId={workspaceId}
            channel={open}
            myUid={me.uid}
            members={members.list}
            byUid={members.byUid}
            readAt={reads.get(open.id)}
          />
        )}
      </div>
    </div>
  );
}
